import { Component, OnInit, OnDestroy} from '@angular/core';
import * as _ from 'lodash';
import * as Chart from 'chart.js';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import CustomerPondModel from '../../../../../services/customerPond.model';
import { CustomerPondService } from '../../../../../services/customerPond.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import PreferencesModel from 'src/app/services/preferences.model';
import { PreferencesService } from 'src/app/services/preferences.service';
import {PersonsService} from '../../../../../services/persons.service';
import PersonsModel from '../../../../../services/persons.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component StockingDensityComponent
 * @selector app-stocking-density
 * @description
 * The `StockingDensityComponent` is a dashboard component within the Farm Metrics section, designed to visualize
 * and report on the stocking density of various ponds. It provides an aggregated view of how many ponds fall
 * into different density ranges (e.g., Low, Medium, High).
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches and aggregates pond data to calculate stocking density summaries across multiple farm sites.
 * 2. **Visualization**: Renders an interactive chart (Doughnut or Bar) using Chart.js to display the distribution of ponds by stocking density.
 * 3. **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (e.g., Farmer, Farm Manager, Provider, Employee).
 * 4. **User Preferences**: Loads and saves the user's preferred chart type (Bar vs. Doughnut) for this specific metric.
 * 5. **Detailed Reporting**: Displays a searchable list of ponds with their individual stocking densities.
 *
 * ## User Roles & Permissions:
 * - **Farmer / Business Admin (Farm)**: View stocking density for their specific farm business.
 * - **Farm Manager**: View metrics for farms assigned to them.
 * - **Business Admin (Provider)**: View metrics for farms associated with the provider.
 * - **Provider Manager**: View metrics for farms assigned to their technicians.
 * - **Employee**: View metrics for all farm sites (internal admin).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessName`: Business name.
 * - `loginBusinessCategory`: Business category.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `sdType`: Saved preference for the stocking density chart type.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Determines user context from `localStorage`.
 *    - Fetches relevant farm sites based on the user's role.
 *    - Calls `getOnLoadCallsData` with the list of farm site IDs.
 * 2. **Data Fetching (`getOnLoadCallsData`)**:
 *    - Fetches stocking density summary and details concurrently from `CustomerPondService`.
 *    - Prepares chart data (`piekeys`, `piedata`).
 *    - Renders the chart (`prepareSDChart`) based on user preferences.
 * 3. **Interaction**:
 *    - The user can switch between 'bar' and 'doughnut' chart types via `graphselection`.
 *    - The user can filter the detailed list using the `searchBusiness` method.
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-stocking-density',
  templateUrl: './stocking-density.component.html',
  styleUrls: ['./stocking-density.component.scss']
})
export class StockingDensityComponent implements OnInit, OnDestroy{
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;
  /** @type {Array<PreferencesModel>} User preferences for chart display settings. */
  public preferences: Array<PreferencesModel>;
  /** @type {Array<CustomerPondModel>} Summary data for stocking density, used for chart aggregation. */
  public customerPonds: Array<CustomerPondModel>;
  /** @type {Array<CustomerPondModel>} List of stocking density data grouped by location. */
  public stockingDensityLocations: Array<CustomerPondModel>;
  /** @type {Array<CustomerPondModel>} Detailed list of ponds and their stocking densities for the table view. */
  public stockingDensityList: Array<CustomerPondModel>;
  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if the page data (charts/lists) has loaded.
   */
  public pgLoad = false;
  /** @type {object} Keys (labels) for the stocking density chart. */
  public piekeys: object;
  /** @type {object} Data values for the stocking density chart. */
  public piedata: object;
  /** @type {string} Title displayed on the chart. */
  public charttitle: string;
  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;
  /** @type {Array<any>} List of farm site IDs used for data fetching. */
  public farmSiteIds = new Array();
  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;
  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;
  /** @type {string} Current chart type for stocking density (e.g., 'bar', 'doughnut'). */
  public sdType: string;
  /** @type {any} Configuration object for the Chart.js chart. */
  public graphconfig: any;
  /** @type {any} Chart.js instance. */
  public graph: any;
  // sdChartTypeData = new Array();
  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if the initial API response has loaded.
   */
  public responseLoad: boolean = false;
  /** @type {string} Search keyword for filtering the list. */
  public search: string;
  /** @type {Array<PersonsModel>} List of technicians (used for Provider Manager role). */
  public technicians: Array<PersonsModel>;
  /** @type {Array<any>} List of technician IDs (for Provider Manager). */
  public technicianIds = new Array();

  /**
   * @constructor
   * @description Initializes the component and injects required services.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {PersonsService} personService - Service for person-related operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {PreferencesService} preferencesService - Service for managing user preferences.
   * @param {CustomerPondService} customerPondService - Service for fetching customer pond data.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   */
  constructor(public sidenavaction: Sidenavaction, public personService: PersonsService, public farmSiteService: FarmSiteService, public preferencesService: PreferencesService, public customerPondService: CustomerPondService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description
   * Initializes the component by performing the following steps:
   * 1. Sets the previous route for navigation history.
   * 2. Retrieves user context (role, business name, category) from `localStorage`.
   * 3. Fetches the user's preferred chart type for stocking density.
   * 4. Determines the user's role and fetches the appropriate list of farm sites.
   * 5. Calls `getOnLoadCallsData` to fetch and display the stocking density metrics.
   *
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.farmSiteIds = new Array();
      // get person list based on login businessid
      if (this.loginPersonType != 'Farmer' && this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Business Admin') {
        this.sdType = localStorage.getItem('sdType');
        if (this.sdType == '') {
          this.sdType = 'bar';
        }
      }
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmsId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByBAdminId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else if (this.loginPersonType == 'Provider Manager') {
        this.technicians = await this.personService.getAllTechniciansByProviderManagerId(localStorage.personID);
        this.technicianIds = this.technicians.map(technician => technician.id);
        this.farmSites = await this.farmSiteService.getFarmeSitesByTechnicianIDs(this.technicianIds);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else {
        this.farmSites = await this.farmSiteService.getAllFarmSites();
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      }
      this.responseLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

/**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description
   * Lifecycle hook that cleans up resources when the component is destroyed.
   * It cancels any ongoing HTTP requests to prevent memory leaks and unnecessary network activity.
   *
   * @returns {void}
   */
public ngOnDestroy(): void {
  this.farmSiteService.cancelOnGoingHttpRequests();
  this.customerPondService.cancelOnGoingHttpRequests();
  this.preferencesService.cancelOnGoingHttpRequests();
}

  /**
   * @method getChattypeinDB
   * @async
   * @description
   * Fetches the user's saved chart type preference from the database.
   * If a preference is found, it sets `sdType` and prepares the chart. If no preference is found, it defaults to 'doughnut'.
   * @returns {Promise<void>}
   * @side-effect Modifies `this.sdType` and calls `prepareSDChart`.
   */
  public async getChattypeinDB() {
    try {
      this.preferences = await this.preferencesService.getPeferencesbyUserIDBusinessID(localStorage.businessID, localStorage.userID);
      if (this.preferences.length > 0) {
        this.sdType = this.preferences[0].chartType;
      } else {
        this.sdType = 'doughnut';
      }
      this.prepareSDChart(this.sdType);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getOnLoadCallsData
   * @async
   * @description
   * Fetches the stocking density summary and detailed list data concurrently.
   * After fetching, it processes the summary data for chart rendering and populates the detailed list.
   * @param {Array<any>} farmSiteIds - An array of farm site IDs to fetch data for.
   * @returns {Promise<void>}
   * @side-effect Populates `customerPonds`, `piekeys`, `piedata`, and `stockingDensityList`. Triggers chart rendering.
   */
  public async getOnLoadCallsData(farmSiteIds) {
    try {
      let promiseResults = new Array();
      await Promise.all([
        await this.customerPondService.getStockingDensitySummary(farmSiteIds),
        await this.customerPondService.getStockingDensityDetails(farmSiteIds)
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        alert(reject);
      });
      this.customerPonds = promiseResults[0];
      if (this.customerPonds !== undefined) {
        this.customerPonds = Object.assign({}, this.customerPonds);
        delete this.customerPonds['pondsCount'];
        this.piekeys = Object.keys(this.customerPonds);
        this.piedata = Object.values(this.customerPonds);
        this.pgLoad = true;
        if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
          this.preferences = await this.preferencesService.getPeferencesbyUserIDBusinessID(localStorage.businessID, localStorage.userID);
          if (this.preferences.length > 0) {
            this.sdType = this.preferences[0].chartType;
          } else {
            this.sdType = 'doughnut';
          }
          this.prepareSDChart(this.sdType);
        } else {
          let feedcharttype = this.sdType || 'doughnut';
          this.prepareSDChart(feedcharttype);
        }
      }
      this.stockingDensityList = promiseResults[1];
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareSDChart
   * @description
   * Configures and renders the stocking density chart using Chart.js.
   * It supports both 'doughnut' and 'bar' chart types and handles cases where no data is available.
   * @param {string} type - The type of chart to render ('doughnut' or 'bar').
   * @example
   * this.prepareSDChart('doughnut');
   * @returns {void}
   */
  public prepareSDChart(type: string) {
    this.charttitle = 'Ponds by density' + ' ' + ' ' + ' ';
    if (this.customerPonds['pondsCount']) {
      this.charttitle += this.customerPonds['pondsCount'] + ' ponds';
    }
    let ctx: any = document.getElementById('myChart');
    let ctx1: any = ctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.piedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let stockingdensitynoData = false;
    if (isallareZeros.length == 0) {
      stockingdensitynoData = true;
    }
    let legenddisply = true;
    if (type == 'bar') {
      legenddisply = false;
    }
    this.graphconfig = {
      data: {
        labels: this.piekeys,
        datasets: [{
          backgroundColor: [
            '#6AD276',
            '#3498db',
            '#FAFF75',
            '#9b59b6',
            '#F0785F'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.piedata
        }]
      },
      options: {
        legend: {
          // display: stockingdensitynoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: stockingdensitynoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2
          },
        },
      }
    };
    let temp = $.extend(true, {}, this.graphconfig);
    temp.type = type || 'doughnut';
    // FOR NO DATA FOUND
    this.graph = new Chart(ctx1, temp);
  }

  /**
   * @method graphselection
   * @description
   * Handles the user's selection of a chart type (Bar or Doughnut).
   * It destroys the existing chart instance and creates a new one with the selected type.
   * It also saves the user's preference to the database or `localStorage`.
   * @param {string} type - The selected chart type ('bar' or 'doughnut').
   * @example
   * this.graphselection('bar');
   * @returns {void}
   * @side-effect Destroys and recreates the chart, calls `charttypesavetoDB` or updates `localStorage`.
   */
  public graphselection(type: string) {
    if (type == 'bar') {
      let ele = '<canvas id=\'myChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('myChart');
      let ctx1: any = ctx.getContext('2d');
      if (this.graph) {
        this.graph.destroy();
      }
      this.graphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.graphconfig);
      temp.type = 'bar';
      this.graph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('sdType', temp.type);
      }
    } else if (type == 'doughnut') {
      let ele = '<canvas id=\'myChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('myChart');
      let ctx1: any = ctx.getContext('2d');
      if (this.graph) {
        this.graph.destroy();
      }
      this.graphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.graphconfig);
      temp.type = 'doughnut';
      this.graph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
      } else {
        localStorage.setItem('sdType', temp.type);
      }
    }
  }

  /**
   * @method charttypesavetoDB
   * @async
   * @description
   * Saves the user's chart type preference to the database via `PreferencesService`.
   * @param {string} type - The chart type to save (e.g., 'bar', 'doughnut').
   * @example
   * await this.charttypesavetoDB('bar');
   * @returns {Promise<void>}
   */
  public async charttypesavetoDB(type: string) {
    try {
      this.preferences = await this.preferencesService.addPreferences(localStorage.businessID, localStorage.userID, type);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllStockingDensityLocationView
   * @async
   * @description
   * Fetches a summary of stocking density data grouped by location.
   * This is typically used for a map view or a location-based breakdown.
   * @returns {Promise<void>}
   * @side-effect Populates `stockingDensityLocations`.
   */
  public async getAllStockingDensityLocationView() {
    try {
      this.stockingDensityLocations = await this.customerPondService.getStockingDensityLocationSummary(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchBusiness
   * @description
   * Filters the displayed list of stocking densities based on the `search` string.
   * This is a client-side filter that uses jQuery to toggle the visibility of table rows.
   * @example
   * // In template: (keyup)="searchBusiness()"
   * this.searchBusiness();
   * @returns {void}
   * @side-effect Toggles the visibility of DOM elements with the class `.searchfun`.
   */
  public searchBusiness() {
    let value = this.search.toLowerCase();
    $('.searchfun').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }
}
